// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * Image Utilities
 *
 * Helper functions for working with images stored in Medplum as Binary resources
 * or Attachment fields on FHIR resources.
 */

import type { Attachment, Organization, Patient, Practitioner } from '@medplum/fhirtypes';
import { ext } from '../config/tenant';

/**
 * Extension URL for organization logos stored in Medplum
 */
export const ORGANIZATION_LOGO_EXTENSION_URL = ext('organization-logo');

/**
 * Legacy extension URL (for backwards compatibility)
 */
const LEGACY_LOGO_EXTENSION_URL = 'http://example.com/fhir/StructureDefinition/organization-logo';

/**
 * Get the logo URL for an organization
 * @param organization - The Organization resource
 * @returns The logo URL or undefined if no logo is set
 */
export function getOrganizationLogoUrl(organization: Organization | null | undefined): string | undefined {
  if (!organization?.extension) {
    return undefined;
  }

  // Check for the new extension URL first, then fall back to legacy
  const logoExtension = organization.extension.find(
    (e) => e.url === ORGANIZATION_LOGO_EXTENSION_URL || e.url === LEGACY_LOGO_EXTENSION_URL
  );

  return logoExtension?.valueUrl;
}

/**
 * Get the photo URL for a patient
 * @param patient - The Patient resource
 * @returns The photo URL or undefined if no photo is set
 */
export function getPatientPhotoUrl(patient: Patient | null | undefined): string | undefined {
  return getAttachmentUrl(patient?.photo?.[0]);
}

/**
 * Get the photo URL for a practitioner (user profile)
 * @param practitioner - The Practitioner resource
 * @returns The photo URL or undefined if no photo is set
 */
export function getPractitionerPhotoUrl(practitioner: Practitioner | null | undefined): string | undefined {
  return getAttachmentUrl(practitioner?.photo?.[0]);
}

/**
 * Get the URL from an Attachment
 * @param attachment - The Attachment object
 * @returns The URL or undefined
 */
export function getAttachmentUrl(attachment: Attachment | undefined): string | undefined {
  if (!attachment) {
    return undefined;
  }

  // Prefer url over data (base64)
  if (attachment.url) {
    return attachment.url;
  }

  // If we only have base64 data, construct a data URL
  if (attachment.data && attachment.contentType) {
    return `data:${attachment.contentType};base64,${attachment.data}`;
  }

  // Some Medplum responses include the URL in a different way
  // Check if there's a reference in the attachment that points to a Binary
  const attachmentAny = attachment as Record<string, unknown>;
  if (attachmentAny.reference && typeof attachmentAny.reference === 'string') {
    return attachmentAny.reference;
  }

  return undefined;
}

/**
 * Get initials from a name string
 * @param name - The full name
 * @returns 1-2 character initials
 */
export function getInitials(name: string | undefined): string {
  if (!name) {
    return '?';
  }

  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }

  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/**
 * Get initials from a Practitioner resource
 */
export function getPractitionerInitials(practitioner: Practitioner | null | undefined): string {
  if (!practitioner?.name?.[0]) {
    return '?';
  }

  const name = practitioner.name[0];
  const first = name.given?.[0]?.charAt(0) || '';
  const last = name.family?.charAt(0) || '';

  return (first + last).toUpperCase() || '?';
}

/**
 * Get initials from a Patient resource
 */
export function getPatientInitials(patient: Patient | null | undefined): string {
  if (!patient?.name?.[0]) {
    return '?';
  }

  const name = patient.name[0];
  const first = name.given?.[0]?.charAt(0) || '';
  const last = name.family?.charAt(0) || '';

  return (first + last).toUpperCase() || '?';
}
